import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const UserManagementTable = ({ users, onUserAction }) => {
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const roleOptions = [
    { value: 'all', label: 'All Roles' },
    { value: 'retail', label: 'Retail User' },
    { value: 'enterprise', label: 'Enterprise Client' },
    { value: 'partner', label: 'Retail Partner' },
    { value: 'admin', label: 'Administrator' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'suspended', label: 'Suspended' },
    { value: 'pending', label: 'Pending' }
  ];

  const filteredUsers = users?.filter(user => {
    const matchesRole = selectedRole === 'all' || user?.role === selectedRole;
    const matchesStatus = selectedStatus === 'all' || user?.status === selectedStatus;
    const matchesSearch = user?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         user?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchesRole && matchesStatus && matchesSearch;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-success/10 text-success';
      case 'suspended': return 'bg-error/10 text-error';
      case 'pending': return 'bg-warning/10 text-warning';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'retail': return 'bg-primary/10 text-primary';
      case 'enterprise': return 'bg-secondary/10 text-secondary';
      case 'partner': return 'bg-accent/10 text-accent';
      case 'admin': return 'bg-destructive/10 text-destructive';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="bg-card rounded-xl shadow-elevation-2 border border-border">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-foreground mb-1">User Management</h2>
            <p className="text-sm text-muted-foreground">Manage user accounts and permissions</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 sm:flex-initial">
              <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full sm:w-64 pl-10 pr-4 py-2 bg-background border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <Select
              options={roleOptions}
              value={selectedRole}
              onChange={setSelectedRole}
              placeholder="Filter by role"
              className="w-full sm:w-40"
            />
            <Select
              options={statusOptions}
              value={selectedStatus}
              onChange={setSelectedStatus}
              placeholder="Filter by status"
              className="w-full sm:w-40"
            />
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-medium text-muted-foreground">User</th>
              <th className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-medium text-muted-foreground">Role</th>
              <th className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-medium text-muted-foreground">Status</th>
              <th className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-medium text-muted-foreground">Last Active</th>
              <th className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filteredUsers?.map((user) => (
              <tr key={user?.id} className="hover:bg-muted/30 transition-smooth">
                <td className="px-4 md:px-6 py-3 md:py-4">
                  <div className="flex items-center gap-3">
                    <Image
                      src={user?.avatar}
                      alt={user?.avatarAlt}
                      className="w-8 h-8 md:w-10 md:h-10 rounded-full object-cover"
                    />
                    <div className="min-w-0">
                      <p className="text-sm md:text-base font-medium text-foreground truncate">{user?.name}</p>
                      <p className="text-xs md:text-sm text-muted-foreground truncate">{user?.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 md:px-6 py-3 md:py-4">
                  <span className={`inline-flex px-2 py-1 md:px-3 md:py-1.5 rounded-full text-xs md:text-sm font-medium ${getRoleBadgeColor(user?.role)}`}>
                    {user?.roleLabel}
                  </span>
                </td>
                <td className="px-4 md:px-6 py-3 md:py-4">
                  <span className={`inline-flex px-2 py-1 md:px-3 md:py-1.5 rounded-full text-xs md:text-sm font-medium ${getStatusColor(user?.status)}`}>
                    {user?.status?.charAt(0)?.toUpperCase() + user?.status?.slice(1)}
                  </span>
                </td>
                <td className="px-4 md:px-6 py-3 md:py-4">
                  <p className="text-xs md:text-sm text-muted-foreground whitespace-nowrap">{user?.lastActive}</p>
                </td>
                <td className="px-4 md:px-6 py-3 md:py-4">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Edit"
                      onClick={() => onUserAction('edit', user?.id)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="MoreVertical"
                      onClick={() => onUserAction('more', user?.id)}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filteredUsers?.length === 0 && (
        <div className="p-8 md:p-12 text-center">
          <Icon name="Users" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <p className="text-sm md:text-base text-muted-foreground">No users found matching your filters</p>
        </div>
      )}
      <div className="p-4 md:p-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-xs md:text-sm text-muted-foreground">
          Showing {filteredUsers?.length} of {users?.length} users
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" iconName="ChevronLeft">Previous</Button>
          <Button variant="outline" size="sm" iconName="ChevronRight">Next</Button>
        </div>
      </div>
    </div>
  );
};

export default UserManagementTable;